import React, { useCallback, RefObject, useEffect } from 'react';
import { formatVideoTime } from '../../utils/formatVideoTime';
import { Button } from '../atomic/buttons';
import { Icons } from '../atomic/icons';
import {
    APIError,
    isMutationSuccess,
    useGet,
    useMutation,
} from '../../hooks/useApi';
import { useErrors } from '../../hooks/useErrors';
import { DialogBox } from '../atomic/dialog';
import { SimpleDialogProps, useDialog } from '../../hooks/useDialog';
import { useNavigateToItem } from '../atomic/links';
import { userApi } from '../../APIs/userApi';
import { FilesAPI } from '../../APIs/filesApi';
import { isVideo } from '../../models/FileModel';
import { usePlayer, usePlayerScrubtime } from '../../contexts/PlayerContext';

export interface ReelScreenshotsProps {
    projectId: string;
    fileId: string;
    sourceFileId: string;
    folderId: string;
    readonly: boolean;
}

const GotoUploadDialog = (opts: SimpleDialogProps) => (
    <DialogBox title="確認" {...opts}>
        アップロードした画像のページを開きますか？
    </DialogBox>
);

const GotoNeighborDialog = (opts: SimpleDialogProps) => (
    <DialogBox title="確認" {...opts}>
        隣接するフレームが既にアップロードされています。
        <br />
        アップロード済みの画像のページを開きますか？
    </DialogBox>
);

interface FileLinkContext {
    fileId: string;
    sourceId: string;
}

const useGotoFileDialog = (dialog: React.FC<SimpleDialogProps>) => {
    const navigateToItem = useNavigateToItem();
    return useDialog<{}, void, FileLinkContext>(
        dialog,
        ({ fileId, sourceId }) => ({
            options: {},
            async onCommit() {
                navigateToItem({ fileId, sourceId });
            },
        }),
        [navigateToItem]
    );
};

export type ReelScreenshotButtonProps = {
    folderId: string;
    sourceId: string;
};

export const ReelScreenshotButton = ({
    folderId,
    sourceId,
}: ReelScreenshotButtonProps) => {
    const source = useGet(FilesAPI.item(sourceId));
    const { appendError } = useErrors();
    const scrubTime = usePlayerScrubtime();
    const player = usePlayer();
    const upload = useMutation(FilesAPI.mutate.startVideoSlice);
    const gotoUpload = useGotoFileDialog(GotoUploadDialog);
    const gotoNeighbor = useGotoFileDialog(GotoNeighborDialog);
    const itemPermission = useGet(userApi.get.itemPermission(folderId));
    const addScreenshot = useCallback(() => {
        if (!player) return;
        if (!source.data) return;
        const videoTimeSeconds = ((scrubTime ?? 0) * 1000) | 0;

        upload.send({
            fileId: sourceId,
            videoTimeSeconds,
        });
    }, [scrubTime, sourceId, source.data]);
    useEffect(() => {
        const { lastResult } = upload;
        if (!lastResult) return;
        if (isMutationSuccess(lastResult)) {
            const {
                fileId,
                folderId,
                parentFileId: sourceId,
            } = lastResult.response;
            FilesAPI.frameIds({ folderId, sourceId }).mutate();
            gotoUpload.show({ fileId: fileId, sourceId });
        } else {
            const { error } = lastResult;
            if (error instanceof APIError && error.status === 409) {
                const fileId = error.data?.fileId;
                if (fileId) {
                    gotoNeighbor.show({ fileId, sourceId });
                    return;
                }
            }
            appendError(
                (error instanceof Error && error?.message) ??
                    'アップロードに失敗しました'
            );
        }
    }, [upload.lastResult]);
    if (
        !isVideo(source.data?.data) ||
        !source.data ||
        !itemPermission.data?.data.write
    ) {
        return <></>;
    }
    return (
        <div className="reel--screenshots">
            {upload.isSending ? (
                <Button disabled icon={Icons.Add}>
                    アップロード中
                </Button>
            ) : (
                <Button onClick={addScreenshot} icon={Icons.Add}>
                    <i className="fa-solid fa-stopwatch"></i>
                    {formatVideoTime(scrubTime ?? 0)}
                </Button>
            )}
            {gotoUpload.dialog}
            {gotoNeighbor.dialog}
        </div>
    );
};
